import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import type { Tab } from './navbar-types';

interface NavbarTabsProps {
  tabs: Tab[];
  isAuthenticated: boolean;
}

export const NavbarTabs = ({ tabs, isAuthenticated }: NavbarTabsProps) => {
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const [forceClosedTab, setForceClosedTab] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);

  const isOpen = (label: string) => hoveredTab === label && forceClosedTab !== label;

  const handleMouseEnter = (label: string) => {
    setHoveredTab(label);
  };

  const handleMouseLeave = (label: string) => {
    setHoveredTab(null);
    // Reset force-closed when mouse leaves, so next hover works normally
    if (forceClosedTab === label) setForceClosedTab(null);
  };

  const handleClick = (label: string) => {
    if (isOpen(label)) {
      // Currently open (via hover or click) → force close it
      setForceClosedTab(label);
    } else {
      // Currently closed → open it and clear any force-close
      setForceClosedTab(null);
      setHoveredTab(label);
    }
  };

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setHoveredTab(null);
        setForceClosedTab(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="navbar-tabs" data-authenticated={isAuthenticated} ref={navRef}>
      {tabs.map((tab) => {
        const open = isOpen(tab.label);

        return (
          <div
            key={tab.label}
            className="navbar-tab-wrapper"
            onMouseEnter={() => tab.subtabs && handleMouseEnter(tab.label)}
            onMouseLeave={() => tab.subtabs && handleMouseLeave(tab.label)}
          >
            {tab.subtabs ? (
              <>
                <div
                  className="navbar-tab"
                  onClick={() => handleClick(tab.label)}
                  aria-expanded={open}
                >
                  <span>{tab.label}</span>
                  <i className="fa fa-angle-down navbar-tab-icon" aria-hidden />
                </div>
                <div className={`navbar-dropdown${open ? ' navbar-dropdown--open' : ''}`}>
                  {tab.subtabs.map((subtab) =>
                    subtab.internal ? (
                      <Link key={subtab.label} href={subtab.href} className="navbar-subtab">
                        {subtab.label}
                      </Link>
                    ) : (
                      <a key={subtab.label} href={subtab.href} className="navbar-subtab">
                        {subtab.label}
                      </a>
                    ),
                  )}
                </div>
              </>
            ) : tab.internal ? (
              <Link href={tab.href ?? '#'} className="navbar-tab navbar-tab-link">
                {tab.label}
              </Link>
            ) : (
              <a href={tab.href} className="navbar-tab navbar-tab-link">
                {tab.label}
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
};
